<script setup lang="ts">
  defineOptions({ name: 'EventDetailTabPanel' })

  import { SearchLgIcon } from '@gohighlevel/ghl-icons/24/outline'

  import { createElementId } from '@common/core-layer/utils'

  import CPButton from '@ui/components/CPButton.vue'
  import CPDivider from '@ui/components/CPDivider.vue'
  import CPInput from '@ui/components/CPInput.vue'
  import CPText from '@ui/components/CPText.vue'

  defineProps({
    idPrefix: {
      type: String,
      required: true,
    },
    title: {
      type: String,
      required: true,
    },
    subtitle: {
      type: String,
      default: '',
    },
    showSearch: {
      type: Boolean,
      default: true,
    },
    searchPlaceholder: {
      type: String,
      default: '',
    },
    searchQuery: {
      type: String,
      default: '',
    },
    footerLabel: {
      type: String,
      default: '',
    },
    showPagination: {
      type: Boolean,
      default: true,
    },
    canGoPrevious: {
      type: Boolean,
      default: false,
    },
    canGoNext: {
      type: Boolean,
      default: false,
    },
    previousLabel: {
      type: String,
      required: true,
    },
    nextLabel: {
      type: String,
      required: true,
    },
  })

  const emit = defineEmits<{
    'update:searchQuery': [value: string]
    previous: []
    next: []
  }>()
</script>

<template>
  <section :id="createElementId(idPrefix, 'tab-panel', 'root')" class="overflow-hidden rounded-xl bg-background shadow-sm">
    <div class="flex flex-col gap-3 px-4 py-4 md:flex-row md:items-center md:justify-between md:gap-4 md:px-6">
      <div class="min-w-0">
        <CPText :id="createElementId(idPrefix, 'tab-panel', 'title')" size="lg" weight="bold" html-tag="h2" class="text-body">
          {{ title }}
        </CPText>
        <CPText
          v-if="subtitle"
          :id="createElementId(idPrefix, 'tab-panel', 'subtitle')"
          size="md"
          weight="normal"
          html-tag="p"
          class="mt-1 text-500"
        >
          {{ subtitle }}
        </CPText>
      </div>

      <div class="flex shrink-0 flex-nowrap items-center gap-2">
        <slot name="actions" />
        <CPInput
          v-if="showSearch"
          :id="createElementId(idPrefix, 'tab-panel', 'search')"
          :model-value="searchQuery"
          :prefix-icon="SearchLgIcon"
          type="text"
          size="sm"
          class="box-border w-[min(100%,16.25rem)] min-w-[12rem] sm:w-[16.25rem]"
          :placeholder="searchPlaceholder"
          @update:model-value="emit('update:searchQuery', String($event ?? ''))"
        />
        <slot name="after-search" />
      </div>
    </div>

    <CPDivider margin-top="0px" margin-bottom="0px" />

    <slot name="filters" />

    <slot />

    <CPDivider margin-top="0px" margin-bottom="0px" />

    <div v-if="footerLabel || showPagination" class="flex flex-wrap items-center gap-3 px-4 py-3 md:px-6">
      <CPText v-if="footerLabel" size="md" weight="normal" class="text-500">
        {{ footerLabel }}
      </CPText>
      <div class="flex-1" />
      <template v-if="showPagination">
        <CPButton
          :id="createElementId(idPrefix, 'tab-panel', 'previous')"
          type="secondary"
          color="secondary"
          size="sm"
          :disabled="!canGoPrevious"
          class="event-detail-page-button"
          @click="emit('previous')"
        >
          {{ previousLabel }}
        </CPButton>
        <CPButton
          :id="createElementId(idPrefix, 'tab-panel', 'next')"
          type="secondary"
          color="secondary"
          size="sm"
          :disabled="!canGoNext"
          class="event-detail-page-button"
          @click="emit('next')"
        >
          {{ nextLabel }}
        </CPButton>
      </template>
    </div>
  </section>
</template>

<style scoped>
  .event-detail-page-button {
    --n-height: 2.25rem;
    --n-font-size: 0.875rem;
    --n-padding: 0 0.875rem;
    --n-border-radius: 0.5rem;
    font-weight: 600;
  }
</style>
